import { Link, useNavigate, useLocation, useParams } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { useUserStore } from '@/stores/userStore'
import { useSubscriptionStore } from '@/stores/subscriptionStore'
import { ROUTES } from '@/utils/constants'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { Alert, Skeleton, Box } from '@mui/material'
import { ImpersonationBanner } from '@/components/users/ImpersonationBanner'
import { SidebarFooter } from '@/components/layout/SidebarFooter'
import { useChatStore, selectHasUnreadMessages } from '@/stores/chatStore'
import { useChatGlobalSync } from '@/realtime/useChatGlobalSync'
import { NotificationBell } from '@/components/notifications/NotificationBell'
import { ActiveLessonBar } from '@/components/lessons/ActiveLessonBar'
import { getUserDisplayName } from '@/utils/userName'
import '@/styles/dashboard.css'

interface DashboardLayoutProps {
  children: React.ReactNode
  /** Снять ограничение max-width каркаса (широкие страницы: дашборд с сеткой виджетов) */
  fullWidth?: boolean
}

export function DashboardLayout({ children, fullWidth = false }: DashboardLayoutProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams<{ id?: string }>()
  const user = useAuthStore((state) => state.user)

  // Разделы карточки ученика: пока путь один из них, левое меню остаётся
  // меню ученика. Список общий с разметкой ниже — забыть тут новый раздел
  // значит уронить пользователя обратно в главное меню на его же странице.
  const USER_CONTEXT_SECTIONS = [
    'cards',
    'tests',
    'settings',
    'activity',
    'finance',
    'schedule',
    'programs',
    'homeworks',
    'language-profile',
  ]

  const isUserContext =
    location.pathname.startsWith('/users/') &&
    params.id &&
    (location.pathname === `/users/${params.id}` ||
     USER_CONTEXT_SECTIONS.some((section) => location.pathname.startsWith(`/users/${params.id}/${section}`)))
  const userContextId = isUserContext ? params.id : null
  const currentUser = useUserStore((state) => state.currentUser)
  const isUserLoading = useUserStore((state) => state.isLoading)
  const viewedStudentName =
    userContextId && currentUser?.id === Number(userContextId)
      ? getUserDisplayName(currentUser, { prefer: 'full_name' })
      : null
  const showStudentNameSkeleton =
    Boolean(userContextId) &&
    (isUserLoading || !currentUser || currentUser.id !== Number(userContextId))
  const subscriptionRequiredMessage = useSubscriptionStore((state) => state.subscriptionRequiredMessage)
  const setSubscriptionRequiredMessage = useSubscriptionStore((state) => state.setSubscriptionRequiredMessage)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isAdminSubmenuOpen, setIsAdminSubmenuOpen] = useState(false)

  const isOnAdminSection = location.pathname.startsWith('/admin/')
  const showAdminSubmenu = isAdminSubmenuOpen || isOnAdminSection
  const hasUnreadChat = useChatStore(selectHasUnreadMessages)

  useChatGlobalSync()

  return (
    <div className="dashboard-layout">
      <ImpersonationBanner />
      {/* Mobile Header */}
      <header className="dashboard-header-mobile">
        <button
          className="menu-toggle"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label={t('dashboard.toggleMenu')}
        >
          <i className="fas fa-bars"></i>
        </button>
        <Link to={ROUTES.DASHBOARD} className="dashboard-logo-mobile">
          <img 
            src="/icon/android-chrome-192x192.png" 
            alt="EASYA" 
            onError={(e) => {
              const target = e.target as HTMLImageElement
              target.src = '/icon/apple-touch-icon.png'
            }} 
          />
          <span>EASYA</span>
        </Link>
        <NotificationBell />
        {/* Открытое меню шапка перекрывает сверху: плашка тогда в самом меню */}
        {!isMenuOpen && <ActiveLessonBar placement="header" />}
      </header>

      <div className={`dashboard-container${fullWidth ? ' dashboard-container--full' : ''}`}>
        {/* Sidebar */}
        <aside className={`dashboard-sidebar ${isMenuOpen ? 'open' : ''}`}>
          {/* Logo */}
          <div className="sidebar-logo">
            <Link to={ROUTES.DASHBOARD} className="dashboard-logo">
              <img 
                src="/icon/android-chrome-192x192.png" 
                alt="EASYA" 
                onError={(e) => {
                  const target = e.target as HTMLImageElement
                  target.src = '/icon/apple-touch-icon.png'
                }} 
              />
              <span>EASYA</span>
            </Link>
            <NotificationBell />
          </div>

          <ActiveLessonBar placement="sidebar" />

          <nav className="dashboard-nav">
            {userContextId ? (
              <>
                <div className="nav-item nav-item-student-name">
                  <button
                    type="button"
                    className="nav-item-student-back"
                    onClick={() => {
                      setIsMenuOpen(false)
                      navigate(ROUTES.USERS)
                    }}
                    aria-label={t('profile.back')}
                  >
                    <i className="fas fa-arrow-left"></i>
                  </button>
                  <i className="fas fa-user nav-item-student-icon" aria-hidden />
                  {showStudentNameSkeleton ? (
                    <span className="nav-item-student-name-text">
                      <Skeleton variant="text" width="70%" height={24} sx={{ flexShrink: 0, bgcolor: 'rgba(255,255,255,0.3)', display: 'block' }} />
                    </span>
                  ) : viewedStudentName ? (
                    <span className="nav-item-student-name-text">{viewedStudentName}</span>
                  ) : null}
                </div>
                <Link
                  to={`/users/${userContextId}`}
                  className={`nav-item ${location.pathname === `/users/${userContextId}` ? 'active' : ''}`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className="fas fa-user"></i>
                  <span>{t('dashboard.profile')}</span>
                </Link>
                <Link
                  to={ROUTES.USER_CARDS(Number(userContextId))}
                  className={`nav-item ${location.pathname === `/users/${userContextId}/cards` ? 'active' : ''}`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className="fas fa-layer-group"></i>
                  <span>{t('dashboard.cards')}</span>
                </Link>
                <Link
                  to={ROUTES.USER_FINANCE(Number(userContextId))}
                  className={`nav-item ${location.pathname === `/users/${userContextId}/finance` ? 'active' : ''}`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className="fas fa-wallet"></i>
                  <span>{t('billing.financeSection', 'Финансы')}</span>
                </Link>
                <Link
                  to={ROUTES.USER_SCHEDULE(Number(userContextId))}
                  className={`nav-item ${location.pathname === `/users/${userContextId}/schedule` ? 'active' : ''}`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className="fas fa-calendar-alt"></i>
                  <span>{t('lessonSchedules.title')}</span>
                </Link>
                <Link
                  to={ROUTES.USER_SETTINGS(Number(userContextId))}
                  className={`nav-item ${location.pathname === `/users/${userContextId}/settings` ? 'active' : ''}`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className="fas fa-cog"></i>
                  <span>{t('users.settings')}</span>
                </Link>
                <Link
                  to={ROUTES.USER_PROGRAMS(Number(userContextId))}
                  className={`nav-item ${location.pathname === `/users/${userContextId}/programs` ? 'active' : ''}`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className="fas fa-graduation-cap"></i>
                  <span>{t('programs.title')}</span>
                </Link>
                {/* Домашки ученика живут здесь, а не в общем меню: раздел
                  «На проверке» показывает только то, что ждёт проверки, а
                  просмотреть все работы ученика и вернуть их на доработку
                  нужно из его карточки */}
                <Link
                  to={ROUTES.USER_HOMEWORKS(Number(userContextId))}
                  className={`nav-item ${location.pathname.startsWith(`/users/${userContextId}/homeworks`) ? 'active' : ''}`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className="fas fa-tasks"></i>
                  <span>{t('homeworks.title')}</span>
                </Link>
                <Link
                  to={ROUTES.USER_ACTIVITY(Number(userContextId))}
                  className={`nav-item ${location.pathname === `/users/${userContextId}/activity` ? 'active' : ''}`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className="fas fa-chart-simple"></i>
                  <span>{t('activity.title')}</span>
                </Link>
                <Link
                  to={ROUTES.USER_TESTS(Number(userContextId))}
                  className={`nav-item ${location.pathname === `/users/${userContextId}/tests` ? 'active' : ''}`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className="fas fa-clipboard-check"></i>
                  <span>{t('tests.userTitle')}</span>
                </Link>
                {/* Языковой профиль отдельным пунктом, а не блоком в карточке:
                    список пройденных тестов и разбор ответов — это страница, а
                    сегодняшний уровень виден в анкете */}
                <Link
                  to={ROUTES.USER_LANGUAGE_PROFILE(Number(userContextId))}
                  className={`nav-item ${location.pathname.startsWith(`/users/${userContextId}/language-profile`) ? 'active' : ''}`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className="fas fa-language"></i>
                  <span>{t('placement.profileTitle')}</span>
                </Link>
              </>
            ) : showAdminSubmenu ? (
              <>
                <div className="nav-item nav-item-student-name">
                  <button
                    type="button"
                    className="nav-item-student-back"
                    onClick={() => {
                      setIsAdminSubmenuOpen(false)
                      if (isOnAdminSection) navigate(ROUTES.DASHBOARD)
                    }}
                    aria-label={t('profile.back')}
                  >
                    <i className="fas fa-arrow-left"></i>
                  </button>
                  <i className="fas fa-cog nav-item-student-icon" aria-hidden />
                  <span className="nav-item-student-name-text">{t('dashboard.administration')}</span>
                </div>
                <Link
                  to={ROUTES.ADMIN_USERS}
                  className={`nav-item ${location.pathname.startsWith('/admin/users') ? 'active' : ''}`}
                  onClick={() => {
                    setIsMenuOpen(false)
                    setIsAdminSubmenuOpen(false)
                  }}
                >
                  <i className="fas fa-users-cog"></i>
                  <span>{t('admin.users.title')}</span>
                </Link>
                <Link
                  to={ROUTES.ADMIN_PAYMENTS}
                  className={`nav-item ${location.pathname === ROUTES.ADMIN_PAYMENTS ? 'active' : ''}`}
                  onClick={() => {
                    setIsMenuOpen(false)
                    setIsAdminSubmenuOpen(false)
                  }}
                >
                  <i className="fas fa-credit-card"></i>
                  <span>{t('admin.payments.title')}</span>
                </Link>
                <Link
                  to={ROUTES.ADMIN_TOPUP_REQUESTS}
                  className={`nav-item ${location.pathname === ROUTES.ADMIN_TOPUP_REQUESTS ? 'active' : ''}`}
                  onClick={() => {
                    setIsMenuOpen(false)
                    setIsAdminSubmenuOpen(false)
                  }}
                >
                  <i className="fas fa-coins"></i>
                  <span>{t('admin.topupRequests.title', 'Заявки на пополнение')}</span>
                </Link>
                <Link
                  to={ROUTES.ADMIN_FINANCE}
                  className={`nav-item ${location.pathname === ROUTES.ADMIN_FINANCE ? 'active' : ''}`}
                  onClick={() => {
                    setIsMenuOpen(false)
                    setIsAdminSubmenuOpen(false)
                  }}
                >
                  <i className="fas fa-file-invoice-dollar"></i>
                  <span>{t('admin.finance.title', 'Финансовый журнал')}</span>
                </Link>
                <Link
                  to={ROUTES.ADMIN_SUBSCRIPTIONS}
                  className={`nav-item ${location.pathname === ROUTES.ADMIN_SUBSCRIPTIONS ? 'active' : ''}`}
                  onClick={() => {
                    setIsMenuOpen(false)
                    setIsAdminSubmenuOpen(false)
                  }}
                >
                  <i className="fas fa-calendar-alt"></i>
                  <span>{t('admin.subscriptions.title')}</span>
                </Link>
                <Link
                  to={ROUTES.ADMIN_PROGRAMS}
                  className={`nav-item ${location.pathname === ROUTES.ADMIN_PROGRAMS ? 'active' : ''}`}
                  onClick={() => {
                    setIsMenuOpen(false)
                    setIsAdminSubmenuOpen(false)
                  }}
                >
                  <i className="fas fa-graduation-cap"></i>
                  <span>{t('admin.programs.title')}</span>
                </Link>
                <Link
                  to={ROUTES.ADMIN_PLACEMENT_BANK}
                  className={`nav-item ${location.pathname === ROUTES.ADMIN_PLACEMENT_BANK ? 'active' : ''}`}
                  onClick={() => {
                    setIsMenuOpen(false)
                    setIsAdminSubmenuOpen(false)
                  }}
                >
                  <i className="fas fa-ruler-combined"></i>
                  <span>{t('admin.placementBank.title')}</span>
                </Link>
                <Link
                  to={ROUTES.ADMIN_PLANS}
                  className={`nav-item ${location.pathname === ROUTES.ADMIN_PLANS ? 'active' : ''}`}
                  onClick={() => {
                    setIsMenuOpen(false)
                    setIsAdminSubmenuOpen(false)
                  }}
                >
                  <i className="fas fa-tags"></i>
                  <span>{t('admin.plans.title')}</span>
                </Link>
                <Link
                  to={ROUTES.ADMIN_NEWS}
                  className={`nav-item ${location.pathname === ROUTES.ADMIN_NEWS ? 'active' : ''}`}
                  onClick={() => {
                    setIsMenuOpen(false)
                    setIsAdminSubmenuOpen(false)
                  }}
                >
                  <i className="fas fa-newspaper"></i>
                  <span>{t('admin.news.title')}</span>
                </Link>
                <Link
                  to={ROUTES.ADMIN_SOCIAL}
                  className={`nav-item ${location.pathname === ROUTES.ADMIN_SOCIAL ? 'active' : ''}`}
                  onClick={() => {
                    setIsMenuOpen(false)
                    setIsAdminSubmenuOpen(false)
                  }}
                >
                  <i className="fas fa-share-nodes"></i>
                  <span>{t('admin.social.title')}</span>
                </Link>
                <Link
                  to={ROUTES.ADMIN_STUDENT_FORM_FIELDS}
                  className={`nav-item ${location.pathname === ROUTES.ADMIN_STUDENT_FORM_FIELDS ? 'active' : ''}`}
                  onClick={() => {
                    setIsMenuOpen(false)
                    setIsAdminSubmenuOpen(false)
                  }}
                >
                  <i className="fas fa-clipboard-list"></i>
                  <span>{t('studentForms.catalogTitle')}</span>
                </Link>
                <Link
                  to={ROUTES.ADMIN_CHAT_STICKERS}
                  className={`nav-item ${location.pathname === ROUTES.ADMIN_CHAT_STICKERS ? 'active' : ''}`}
                  onClick={() => {
                    setIsMenuOpen(false)
                    setIsAdminSubmenuOpen(false)
                  }}
                >
                  <i className="fas fa-face-smile"></i>
                  <span>{t('admin.chatStickers.title')}</span>
                </Link>
              </>
            ) : (
              <>
                <Link
                  to={ROUTES.DASHBOARD}
                  className={`nav-item ${location.pathname === ROUTES.DASHBOARD ? 'active' : ''}`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className="fas fa-home"></i>
                  <span>{t('dashboard.home')}</span>
                </Link>
                <Box
                  component={Link}
                  to={ROUTES.CHAT}
                  className={`nav-item nav-item--with-indicator ${
                    location.pathname === ROUTES.CHAT || location.pathname.startsWith('/chat/')
                      ? 'active'
                      : ''
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <i className="fas fa-comments"></i>
                  <span>{t('chat.title')}</span>
                  {hasUnreadChat && (
                    <Box
                      aria-label={t('chat.unreadIndicator')}
                      className="nav-item-indicator-dot"
                      sx={{ bgcolor: 'secondary.main', boxShadow: 1 }}
                    />
                  )}
                </Box>
                {user?.roles?.some((role) => role.slug === 'teacher') && (
                  <>
                    <Link
                      to={ROUTES.PROGRAMS}
                      className={`nav-item ${location.pathname.startsWith(ROUTES.PROGRAMS) ? 'active' : ''}`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <i className="fas fa-graduation-cap"></i>
                      <span>{t('programs.title', 'Программы обучения')}</span>
                    </Link>
                    <Link
                      to={ROUTES.EXERCISE_REVIEW}
                      // Проверка пункта домашки живёт по адресу ученика, но это
                      // тот же раздел: без неё сайдбар гаснет посреди проверки
                      className={`nav-item ${
                        location.pathname.startsWith(ROUTES.EXERCISE_REVIEW) ||
                        /\/homeworks\/\d+\/items\/\d+\/check$/.test(location.pathname)
                          ? 'active'
                          : ''
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <i className="fas fa-clipboard-check"></i>
                      <span>{t('exercises.review.title', 'На проверке')}</span>
                    </Link>
                    {/* Библиотека диктантов: заводится в конструкторе задания,
                      но живёт своей жизнью — переиспользование между темами и
                      управление озвучкой, за которую списывается место */}
                    <Link
                      to={ROUTES.EXERCISE_BANK}
                      // Сравнение точное, а не startsWith: банк живёт по '/exercises',
                      // и его префикс перекрывает вложенный раздел '/exercises/review' —
                      // при startsWith на проверке подсвечивались бы сразу два пункта.
                      // Своих вложенных маршрутов у банка в роутере нет.
                      className={`nav-item ${location.pathname === ROUTES.EXERCISE_BANK ? 'active' : ''}`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <i className="fas fa-list-check"></i>
                      <span>{t('exercises.bank.title')}</span>
                    </Link>
                    <Link
                      to={ROUTES.DICTIONARIES}
                      className={`nav-item ${location.pathname === ROUTES.DICTIONARIES ? 'active' : ''}`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <i className="fas fa-book"></i>
                      <span>{t('dashboard.dictionaries')}</span>
                    </Link>
                    <Link
                      to={ROUTES.WORDS}
                      className={`nav-item ${location.pathname === ROUTES.WORDS ? 'active' : ''}`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <i className="fas fa-font"></i>
                      <span>{t('dashboard.words')}</span>
                    </Link>
                    <Link
                      to={ROUTES.TAGS}
                      className={`nav-item ${location.pathname === ROUTES.TAGS ? 'active' : ''}`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <i className="fas fa-tags"></i>
                      <span>{t('dashboard.tags')}</span>
                    </Link>
                  </>
                )}
                {user?.roles?.some((role) => role.slug === 'student') && (
                  <>
                    <Link
                      to={ROUTES.MY_PROGRAMS}
                      className={`nav-item ${location.pathname.startsWith(ROUTES.MY_PROGRAMS) ? 'active' : ''}`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <i className="fas fa-graduation-cap"></i>
                      <span>{t('programs.my.title', 'Моя программа')}</span>
                    </Link>
                    <Link
                      to={ROUTES.CARDS}
                      className={`nav-item ${location.pathname === ROUTES.CARDS ? 'active' : ''}`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <i className="fas fa-layer-group"></i>
                      <span>{t('dashboard.cards')}</span>
                    </Link>
                    <Link
                      to={ROUTES.TESTS}
                      className={`nav-item ${location.pathname === ROUTES.TESTS || location.pathname.startsWith('/tests/') ? 'active' : ''}`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <i className="fas fa-clipboard-check"></i>
                      <span>{t('tests.title')}</span>
                    </Link>
                    <Link
                      to={ROUTES.LESSONS}
                      className={`nav-item ${
                        location.pathname === ROUTES.LESSONS || /^\/lessons\/\d+$/.test(location.pathname)
                          ? 'active'
                          : ''
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <i className="fas fa-chalkboard-teacher"></i>
                      <span>{t('lessons.title')}</span>
                    </Link>
                    <Link
                      to={ROUTES.HOMEWORKS}
                      className={`nav-item ${location.pathname === ROUTES.HOMEWORKS ? 'active' : ''}`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <i className="fas fa-tasks"></i>
                      <span>{t('homeworks.title')}</span>
                    </Link>
                    <Link
                      to={ROUTES.PLACEMENT}
                      className={`nav-item ${location.pathname === ROUTES.PLACEMENT ? 'active' : ''}`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <i className="fas fa-signal"></i>
                      <span>{t('placement.title')}</span>
                    </Link>
                    {/* Свой уровень с историей измерений. Адрес отдельный, а не
                        /users/{id}/language-profile: тот включает учительский
                        контекст просмотра ученика, и ученик получал бы чужое
                        меню — «Настройки ученика», «Финансы», «Тесты ученика» */}
                    <Link
                      to={ROUTES.MY_LEVEL}
                      className={`nav-item ${location.pathname === ROUTES.MY_LEVEL ? 'active' : ''}`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <i className="fas fa-chart-line"></i>
                      <span>{t('placement.myLevel')}</span>
                    </Link>
                  </>
                )}
                {user?.roles?.some((role) => role.slug === 'teacher') && (
                  <>
                    <Link
                      to={ROUTES.GROUPS}
                      className={`nav-item ${location.pathname === ROUTES.GROUPS ? 'active' : ''}`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <i className="fas fa-users"></i>
                      <span>{t('dashboard.groups')}</span>
                    </Link>
                    <Link
                      to={ROUTES.USERS}
                      className={`nav-item ${location.pathname === ROUTES.USERS ? 'active' : ''}`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <i className="fas fa-user-friends"></i>
                      <span>{t('dashboard.students')}</span>
                    </Link>
                    <Link
                      to={ROUTES.LESSONS}
                      className={`nav-item ${
                        location.pathname === ROUTES.LESSONS || /^\/lessons\/\d+$/.test(location.pathname)
                          ? 'active'
                          : ''
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <i className="fas fa-chalkboard-teacher"></i>
                      <span>{t('lessons.title')}</span>
                    </Link>
                    {/* Отдельного пункта «Домашние задания» у учителя нет: всё,
                      что нужно проверить — работы с урока, самопрактика и пункты
                      домашки, — живёт одним списком в разделе «На проверке».
                      Домашки конкретного ученика открываются из его карточки —
                      пункт «Домашние задания» в контекстном меню ученика выше. */}
                    {/* Бухгалтерия — личный раздел учителя, живёт в меню профиля (SidebarFooter) */}
                  </>
                )}
                {user?.roles?.some((role) => role.slug === 'admin') && (
                  <button
                    type="button"
                    className="nav-item"
                    onClick={() => setIsAdminSubmenuOpen(true)}
                  >
                    <i className="fas fa-cog"></i>
                    <span>{t('dashboard.administration')}</span>
                  </button>
                )}
              </>
            )}
          </nav>

          {/* Footer with Language Switcher and Profile */}
          <SidebarFooter onMenuClose={() => setIsMenuOpen(false)} />
        </aside>

        {/* Main Content */}
        <main className="dashboard-main">
          {subscriptionRequiredMessage && user?.roles?.some((r) => r.slug === 'student') && (
            <Alert
              severity="warning"
              onClose={() => setSubscriptionRequiredMessage(null)}
              sx={{ mx: 2, mt: 2 }}
            >
              {subscriptionRequiredMessage}
            </Alert>
          )}
          {children}
        </main>
      </div>

      {/* Overlay for mobile */}
      {isMenuOpen && (
        <div
          className="dashboard-overlay"
          onClick={() => setIsMenuOpen(false)}
        />
      )}
    </div>
  )
}
